/**
 * @file: Reservation.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * Frontend domain types for the reservation surface (backend `reservation`).
 */

import { CustomerType } from '@euler/model/enum';

export type ReservationStatus =
  | 'pending'
  | 'confirmed'
  | 'arrived'
  | 'converted'
  | 'cancelled'
  | 'no_show';

export const TerminalReservationStatuses: ReadonlySet<ReservationStatus> =
  new Set<ReservationStatus>(['converted', 'cancelled', 'no_show']);

export const isTerminalReservationStatus = (
  status: ReservationStatus,
): boolean => TerminalReservationStatuses.has(status);

export const ReservationStatusLabel: Record<ReservationStatus, string> = {
  pending: '待确认',
  confirmed: '已确认',
  arrived: '已到店',
  converted: '已开单',
  cancelled: '已取消',
  no_show: '未到店',
};

export interface Reservation {
  id: string;
  orgId: number;
  storeId: number;
  leadId?: string | null;
  customerId?: string | number | null;
  customerName?: string | null;
  customerPhone?: string | null;
  licensePlateNo: string;
  vin?: string | null;
  currentMileage?: number | null;
  vehicleName: string;
  vehicleBrandName: string;
  vehicleManufacturer?: string | null;
  vehicleModel: string;
  vehicleModelYear?: number | null;
  vehicleDisplacement?: string | null;
  vehicleTrimLevel?: string | null;
  vehicleFuelType?: string | null;
  expectedArrivalAt: string | Date;
  // The `、`-joined name summary derived from `items` (legacy/quick display).
  expectedItems?: string | null;
  // The structured booked service items, as a raw JSON string (parse via
  // `parseReservationItems`). Source of truth for the items panel.
  items?: string | null;
  // Order-intake captured at booking, carried into the order at creation time.
  serviceAgentId: number;
  serviceAgentName: string;
  tags: string;
  customerType?: CustomerType | null;
  vehicleMileage?: number | null;
  remark?: string | null;
  status: ReservationStatus;
  orderNo?: string | null;
  createdAt: string | Date;
  createdBy: number;
  createdByName: string;
  updatedAt?: string | Date | null;
  updatedBy?: number | null;
  updatedByName?: string | null;
}
